<script lang="ts">
  import CategoryCard from '$components/category-card.svelte';
  import { cn } from '$lib/utils';
  import type {
    AssetStoryblok,
    ConfigurationStoryblok,
    NavigationGroupStoryblok,
    RegionalFeaturedPageStoryblok
  } from '$types/bloks';
  import { onMount } from 'svelte';
  import { getAnchorFromCmsLink, getImageAttributes } from '$lib/utils/cms';
  import Link from '$components/ui/link.svelte';
  import { region } from '$lib/region';

  export let configuration: ConfigurationStoryblok;
  let headerContent: ConfigurationStoryblok['header_content'];
  let mainCategories: NavigationGroupStoryblok[] = [];
  let otherCategories: RegionalFeaturedPageStoryblok[] = [];

  onMount(() => {
    headerContent = configuration?.header_content || [];

    if (headerContent) {
      const tempMainCategories: NavigationGroupStoryblok[] = [];
      const tempOtherCategories: RegionalFeaturedPageStoryblok[] = [];

      headerContent.forEach((item) => {
        if (
          item.component === ('navigation-group' as NavigationGroupStoryblok['component']) &&
          item.regions?.includes($region) &&
          item.show_on_desktop
        ) {
          tempMainCategories.push(item as NavigationGroupStoryblok);
        }
        if (
          item.component ===
            ('regional-featured-page' as RegionalFeaturedPageStoryblok['component']) &&
          item.regions?.includes($region) &&
          item.show_on_desktop
        ) {
          tempOtherCategories.push(item as RegionalFeaturedPageStoryblok);
        }
      });

      mainCategories = tempMainCategories;
      otherCategories = tempOtherCategories;
    }
  });

  const getImage = (
    desktopImage: AssetStoryblok | undefined,
    mobileImage: AssetStoryblok | undefined
  ) => {
    const featuredImage = desktopImage || mobileImage;

    if (!featuredImage || !featuredImage?.filename) {
      return { src: '', alt: '' };
    }

    const { src, alt } = getImageAttributes(featuredImage, {
      size: [300, 0]
    });

    return { src, alt };
  };
</script>

<div class="container relative mx-auto flex h-full gap-4 overflow-y-auto py-8">
  <div class="flex h-[260px] w-full gap-4">
    <div class="flex flex-1 grow gap-14">
      {#each mainCategories as story}
        <div class="w-auto h-full" style:--titleColor={story.title_color}>
          <p class={cn('text-primary text-sm mb-4', story.title_color && 'text-[--titleColor]')}>
            {story.title}
          </p>
          {#if story.content && story.content.length > 0}
            <ul class="h-full w-full flex flex-col gap-2.5">
              {#each story.content as link}
                {#if link.url}
                  {@const { href } = $getAnchorFromCmsLink(link.url)}
                  <li>
                    <Link {href} target={link.open_new_tab ? '_blank' : undefined}
                      >{link.label}</Link
                    >
                  </li>
                {/if}
              {/each}
            </ul>
          {/if}
        </div>
      {/each}
    </div>

    <div class="flex h-full gap-4">
      {#each otherCategories as story}
        {@const featurePage = (story.feature_page || [])[0]}
        {@const { href } = $getAnchorFromCmsLink(featurePage?.link)}
        {#if featurePage?.image?.filename || featurePage?.mobile_image?.filename}
          {@const { src, alt } = getImage(featurePage?.image, featurePage?.mobile_image)}
          {#if href}
            <CategoryCard
              size="sm"
              {href}
              open_new_tab={featurePage.open_new_tab}
              label={featurePage.name || ''}
              image={{ src, alt }}
              class="aspect-[9/13]"
            />
          {/if}
        {/if}
      {/each}
    </div>
  </div>
</div>
